import {
  useIsMobile,
  WSButton,
  WSContainer,
  WSDivider,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSScreen,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { ReactNode } from "react";
import { useWSStore } from "../../store";
import { useBrowserPageTitle } from "../BrowserPageTitle/BrowserPageTitle";
import { Fullscreen } from "../Fullscreen/Fullscreen";
import { ResponsiveWingspanLogo } from "../ResponsiveWingspanLogo/ResponsiveWingspanLogo";
import styles from "./SetupFlowLayout.module.scss";

export type SetupLayoutProps = {
  onBack?: () => void;
  title?: string;
  description?: ReactNode;
  currentStep?: number;
  totalSteps?: number;
  banner?: ReactNode;
} & WSElementProps;

export const SetupLayout: React.FC<SetupLayoutProps> = ({
  onBack,
  currentStep,
  totalSteps,
  children,
  title,
  description,
  banner,
  ...props
}) => {
  const isMobile = useIsMobile();
  useBrowserPageTitle(title ?? "Wingspan");
  const {
    appContext: { isEmbedded }
  } = useWSStore();

  return (
    <Fullscreen>
      {!isEmbedded ? (
        <>
          <WSElement className={styles.header} {...props}>
            <WSContainer>
              <WSElement className={styles.headerInner}>
                <WSScreen.Mobile>
                  {onBack && (
                    <WSButton.Link
                      icon="chevronLeft"
                      onClick={onBack}
                      className={styles.backButton}
                      color="grey600"
                    >
                      Back
                    </WSButton.Link>
                  )}
                </WSScreen.Mobile>
                <ResponsiveWingspanLogo />
              </WSElement>
            </WSContainer>
          </WSElement>
        </>
      ) : (
        <>
          <WSElement p="S">
            <WSFlexBox.Center>
              <WSText mr="S">Powered by</WSText>
              <ResponsiveWingspanLogo inline />
            </WSFlexBox.Center>
          </WSElement>
        </>
      )}

      {banner ? (
        <WSElement className={styles.banner}>
          <WSContainer>
            <WSElement className={styles.bannerInner}>{banner}</WSElement>
          </WSContainer>
        </WSElement>
      ) : (
        <WSDivider />
      )}

      <WSContainer>
        <WSElement py={isMobile ? "M" : "XL"}>
          {!isEmbedded && (
            <WSScreen.TabletAndDesktop>
              {onBack && (
                <WSButton.Link
                  icon="chevronLeft"
                  onClick={onBack}
                  className={styles.backButton}
                  color="grey600"
                >
                  Back
                </WSButton.Link>
              )}
            </WSScreen.TabletAndDesktop>
          )}
          <WSElement
            className={styles.content}
            data-testid={`step${currentStep}Content`}
          >
            {currentStep && (
              <WSText.Label color="grey700">
                {currentStep} of {totalSteps}
              </WSText.Label>
            )}

            {title && (
              <WSText.Heading2
                mb={description ? "XS" : isMobile ? "M" : "L"}
                data-testid="heading"
              >
                {title}
              </WSText.Heading2>
            )}

            {description && (
              <WSText.Paragraph mb={isMobile ? "M" : "L"}>
                {description}
              </WSText.Paragraph>
            )}

            {children}
          </WSElement>
        </WSElement>
      </WSContainer>
    </Fullscreen>
  );
};
